/**
 * Operator window -> guest window link.
 *
 * The guest screen lives in a second browser window on the guest-facing
 * monitor, so the QR has to cross a window boundary. BroadcastChannel is the
 * direct equivalent of the desktop app's second WPF window and needs no server
 * round-trip — which matters, because this fires while the venue's internet may
 * well be down.
 */

export type GuestMessage =
  | { type: 'qr.show'; dataUrl: string; url?: string }
  | { type: 'qr.hide' }

const CHANNEL = 'citaria-guest'

export function publishToGuest(message: GuestMessage) {
  const channel = new BroadcastChannel(CHANNEL)
  channel.postMessage(message)
  channel.close()
}

export function subscribeToGuest(handler: (message: GuestMessage) => void): () => void {
  const channel = new BroadcastChannel(CHANNEL)
  channel.onmessage = (event) => handler(event.data as GuestMessage)
  return () => channel.close()
}

/**
 * The console does NOT open the guest window.
 *
 * It used to, from the print action, via `window.open('/guest', …)`. Three
 * things were wrong with that. It raised the window over the console at the
 * moment the operator was starting the next guest. It gave an operator running
 * on a single monitor a popup they never asked for. And when a kiosk profile or
 * an enterprise policy blocked popups, `window.open` returned null and the
 * guest-facing screen silently did not exist.
 *
 * So the operator opens it: a second browser window on the guest monitor, at
 * `/guest`. That is also the only way to get it genuinely fullscreen on a second
 * display, which a popup cannot do. This module just broadcasts — the QR is
 * published whether or not anything is listening, and it is on the printed
 * sheet and on the finish screen regardless.
 */
